import { ReactNode } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { Label } from 'design-system/components/Text';
import { TransitionDuration } from 'design-system/variables/transitions';
import useToggle from 'lib/hooks/useToggle';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 200px;
`;

const LabelContainer = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 16px;
  width: 100%;
`;

const BarContainer = styled.div`
  flex: none;
  position: relative;
  height: 16px;
  width: 100%;
`;

const BarBg = styled.div`
  position: absolute;
  top: 4px;
  left: 0;
  height: 8px;

  width: 100%;

  ${({ theme }) => css`
    border-radius: ${theme.element.borderRadius};
    background-color: ${theme.color.background.disabled};
  `}
`;

const moveAnimation = () => keyframes`
  0% {
    background-position: calc(var(--totalIntervalLength)/var(--sinAngle)) 0;
  }
`;

const BarFg = styled(BarBg)<
  Pick<
    PercentFillBarProps,
    'percent' | 'color' | 'transitionDuration' | 'secondaryColor' | 'striped'
  > & { animating: boolean }
>`
  /* CSS variables for stripes and animation*/
  --primaryStripeLength: 10px;
  --secondaryStripeLength: 10px;
  --totalIntervalLength: calc(var(--primaryStripeLength) + var(--secondaryStripeLength));
  --sinAngle: 0.7071; /* precompute sin(45 degrees) */

  ${({ animating, percent, transitionDuration, striped }) => css`
    animation: ${striped
        ? css`
            ${moveAnimation()} 2s linear infinite reverse
          `
        : ''}
      ${animating ? css`, ${getGrowAnimation(percent)} ${transitionDuration} ease-in-out` : ''};
  `}

  ${({ theme, percent, color, transitionDuration }) => css`
    transition: ${transitionDuration} ease-in-out;
    background-color: ${color != null ? color : theme.color.primary.brand400};
    width: ${(100 * percent).toFixed(1)}%;
  `}

  ${({ theme, color, striped, secondaryColor }) =>
    striped &&
    css`
      background: repeating-linear-gradient(
        45deg,
        ${color || theme.color.primary.brand500},
        ${color || theme.color.primary.brand500} var(--primaryStripeLength),
        ${secondaryColor || theme.color.primary.brand400} var(--primaryStripeLength),
        ${secondaryColor || theme.color.primary.brand400} var(--totalIntervalLength)
      );
      background-size: calc(var(--totalIntervalLength) / var(--sinAngle)) 100%;
    `}
`;

const getGrowAnimation = (percent: PercentFillBarProps['percent']) => keyframes`
  0% {
    width: 0;
  }

  100% {
    width: ${(100 * percent).toFixed(1)}%
  }
`;

const Benchmark = styled.div<Pick<PercentFillBarProps, 'percent' | 'transitionDuration'>>`
  position: absolute;
  top: 2px;
  left: -2px;
  height: 12px;

  ${({ theme, percent, transitionDuration }) => css`
    transition: ${transitionDuration} ease-in-out;
    margin-left: ${(100 * percent).toFixed(1)}%;
    background-color: ${theme.color.text.primary};
    border-left: 1px solid ${theme.color.text.primary};
    border-right: 1px solid ${theme.color.text.primary};
  `}
`;

interface PercentFillBarProps {
  'data-test'?: string;
  benchmark?: number;
  className?: string;
  color?: string;
  secondaryColor?: string; // for stripes
  id?: string;
  label: ReactNode;
  labelRight?: ReactNode;
  percent: number;
  transitionDuration?: string;
  striped?: boolean;
}

const PercentFillBar = ({
  'data-test': dataTest,
  benchmark,
  className,
  color,
  secondaryColor,
  id,
  label,
  labelRight,
  percent,
  transitionDuration = TransitionDuration.Medium,
  striped = false,
}: PercentFillBarProps) => {
  const [animating, { disable: stopAnimating }] = useToggle(true);

  return (
    <Container
      id={id}
      data-test={dataTest}
      className={className}
      aria-valuemin={0}
      aria-valuemax={1}
      aria-valuenow={percent}
    >
      <LabelContainer>
        <Label size='sm'>{label}</Label>
        {labelRight != null && <Label size='sm'>{labelRight}</Label>}
      </LabelContainer>
      <BarContainer>
        <BarBg />
        <BarFg
          data-test='percent-fill-bar__fg'
          percent={percent}
          color={color}
          secondaryColor={secondaryColor}
          animating={animating}
          onAnimationEnd={stopAnimating}
          transitionDuration={transitionDuration}
          striped={striped}
        />
        {benchmark != null && (
          <Benchmark
            data-test='percent-fill-bar__benchmark'
            percent={benchmark}
            transitionDuration={transitionDuration}
          />
        )}
      </BarContainer>
    </Container>
  );
};

export default PercentFillBar;
